if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.cache = 'no-store';
        config.credentials = 'same-origin';
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.cart = this.cart || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', routes.cart_url || window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const requestedQuantity = formData.get('quantity') || 1;
        const cartMonitor = window.SPCLSTCartMonitor;
        const addBaseline =
          cartMonitor && typeof cartMonitor.captureAddBaseline === 'function'
            ? cartMonitor.captureAddBaseline({
                source: 'product-form',
                requestedQuantity,
              })
            : {};
        const cartSessionDetails = {
          source: 'product-form',
          variantId: formData.get('id'),
          quantity: requestedQuantity,
          formDataEntries: Array.from(formData.entries()),
          ...addBaseline,
        };
        let shouldClearCartEmptyState = false;
        const cartDebug = window.SPCLSTCartDebug;
        if (cartDebug && cartDebug.enabled()) {
          cartDebug.log('cart add submit', {
            productVariantId: formData.get('id'),
            sections: formData.get('sections'),
            sections_url: formData.get('sections_url'),
          });
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (cartDebug && cartDebug.enabled()) {
              cartDebug.log('cart add response', {
                productVariantId: formData.get('id'),
                response: {
                  status: response.status || null,
                  id: response.id || null,
                  variant_id: response.variant_id || null,
                  quantity: response.quantity || null,
                  item_count: response.item_count,
                  token: cartDebug.summarizeToken(response.token),
                  hasKey: !!response.key,
                  sections: cartDebug.summarizeSections(response.sections),
                },
              });
              cartDebug.logCartState('cart state after add response');
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              const cartSessionSync = window.SPCLSTCartSession && typeof window.SPCLSTCartSession.syncAfterAdd === 'function'
                ? window.SPCLSTCartSession.syncAfterAdd({
                    ...cartSessionDetails,
                    addResponse: response,
                  })
                : Promise.resolve({ recovered: false });

              cartSessionSync.catch((error) => {
                if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
                  window.SPCLSTCartDebug.log('cart session add sync background failed', {
                    error: error.message,
                  });
                }
              });

              this.waitForCartSurface()
                .then((cartSurface) => {
                  this.cart = cartSurface;
                  if (typeof cartSurface.renderContents === 'function') {
                    cartSurface.renderContents(response);
                  } else if (typeof cartSurface.open === 'function') {
                    cartSurface.open(this.submitButton);
                  }
                })
                .catch(() => {
                  console.warn('[SPCLST cart] Product added, but cart drawer was not available; staying on the current page.');
                });
              return;
            }

            const cartSessionSync = window.SPCLSTCartSession && typeof window.SPCLSTCartSession.syncAfterAdd === 'function'
              ? window.SPCLSTCartSession.syncAfterAdd({
                  ...cartSessionDetails,
                  addResponse: response,
                })
              : Promise.resolve({ recovered: false });
            shouldClearCartEmptyState = true;
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const syncBackgroundCartResult = (sessionResult = {}) => {
              if (!sessionResult.cart || !this.cart || typeof this.cart.syncCartSummary !== 'function') return;
              const itemCount = Number(sessionResult.cart.item_count || 0);
              if (itemCount <= 0) return;
              this.cart.syncCartSummary(sessionResult.cart, itemCount);
              if (sessionResult.recovered && typeof this.cart.refreshFromCart === 'function') {
                this.cart.refreshFromCart();
              }
            };
            const renderUpdatedCart = () => {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });

              cartSessionSync
                .then(syncBackgroundCartResult)
                .catch((error) => {
                  if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
                    window.SPCLSTCartDebug.log('cart session add sync background failed', {
                      error: error.message,
                    });
                  }
                });

              return Promise.resolve();
            };

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    renderUpdatedCart();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderUpdatedCart();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && shouldClearCartEmptyState) {
              this.cart.classList.remove('is-empty');
              this.cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
              this.cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');
              this.cart.querySelector('.drawer__footer')?.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      waitForCartSurface() {
        return new Promise((resolve, reject) => {
          let attempts = 0;
          const findCartSurface = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const check = () => {
            const cartSurface = findCartSurface();
            if (cartSurface) {
              resolve(cartSurface);
              return;
            }
            attempts += 1;
            if (attempts >= 20) {
              reject(new Error('Cart surface unavailable'));
              return;
            }
            window.setTimeout(check, 100);
          };

          check();
        });
      }
    }
  );
}
